import { Box, useToast } from '@chakra-ui/react';
import { TableBuild } from 'components/organisms';
import { TableBuildActionsType } from 'components/organisms/TableBuild/type';
import { useInstitutionsContext } from 'contexts/institutions';
import { motion } from 'framer-motion';
import { HistorySearchRequestDTO, HistorySearchResponseDTO } from 'models';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ContractService from 'services/contracts';
import formatter from 'utils/formatter';
import { removeEmptyValues } from 'utils/removeEmptyValues';
import { FilterHistory } from '../../FilterHistory';

interface HistoryTabProps {
  activeTabIndex: number;
}

export const HistoryTab = ({ activeTabIndex }: HistoryTabProps) => {
  const [history, setHistory] = useState<HistorySearchResponseDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastFilters, setLastFilters] = useState<Omit<HistorySearchRequestDTO, 'pagina' | 'limite'>>({});
  const { currentInstitution } = useInstitutionsContext()
  const toast = useToast()

  const performSearch = useCallback(
    async (filters: Omit<HistorySearchRequestDTO, 'pagina' | 'limite'>, page = 1, limit = 10) => {
      setIsLoading(true);
      setIsError(false);
      try {
        const searchParams = { ...filters, pagina: page, limite: limit };
        const response = await ContractService.getHistory(searchParams, currentInstitution?.id);
        const { data, headers } = response;
        
        const newTotalPages = headers['total-pages'] ? Number(headers['total-pages']) : 1;

        setHistory(data);
        setTotalPages(newTotalPages);
        setCurrentPage(page); 
        setLastFilters(filters);
      } catch (error: any) {
        const apiErrorMessage = error.response?.data?.mensagem;
        toast({
            title: 'Erro ao buscar histórico!',
            description: apiErrorMessage + ' Entre em contato com o administrador do sistema.',
            status: 'error',
        })
        setIsError(true);
        setHistory([]);
        setTotalPages(0);
      } finally {
        setIsLoading(false);
      }
    },
    [currentInstitution?.id]
  );

  const handlePaginate = (page: number, limit: number) => {
    performSearch(lastFilters, page, limit);
  };

  const handleSearch = (data: Omit<HistorySearchRequestDTO, 'pagina' | 'limite'>) => {
    const filteredData = removeEmptyValues(data);
    performSearch(filteredData, 1); 
  };

  useEffect(() => {
    if (activeTabIndex === 2) {
      performSearch({}, 1, 10);
    }  
  }, [activeTabIndex, performSearch, currentInstitution?.id]);
  
  const tableData = useMemo(() => {
    const header = [
      { name: 'uf', text: 'UF', position: 1 },
      { name: 'dataCriacao', text: 'Data da Consulta', position: 2 },
      { name: 'usuario', text: 'Usuário', position: 3 },
      { name: 'chassi', text: 'Chassi', position: 4 },
      { name: 'numeroContrato', text: 'Nº do Contrato', position: 5 }
    ];

    const body = history.map(item => ({
      uf: item.uf ?? '–',
      id: item.guid,
      dataCriacao: formatter.data(item.dataCriacao),
      usuario: item.usuario,
      chassi: item.chassi,
      numeroContrato: item.numeroContrato,
    }));

    const action: TableBuildActionsType[] = [];

    return { header, body, action };
  }, [history]);

  return (
    <Box
      as={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      mt={4}
    >
      <FilterHistory onSearch={handleSearch} isLoading={isLoading} />

      <TableBuild
        header={tableData.header}
        body={tableData.body}
        loading={{
          status: isLoading,
          loader: [{ height: '20px' }, { height: '20px' }, { height: '20px' }]
        }}
        pagination={{
          page: currentPage,
          limit: 10,
          totalPages
        }}
        onChangePage={page => handlePaginate(page, 10)}
        onChangeLimit={limit => handlePaginate(1, limit)}
      />
      {isError && (
        toast({
            title: 'Sentimos muito!',
            description: 'Ocorreu erro ao buscar histórico. Entre em contato com o administrador do sistema.',
            status: 'error',
        })
      )}
    </Box>
  );
};